import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  FormGroup,
} from '@angular/forms';
import { NzModalRef, NZ_MODAL_DATA, NzModalModule } from 'ng-zorro-antd/modal';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzFormModule } from 'ng-zorro-antd/form';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { Bank, NotificationService, User, UserService } from '@beautyapp/core';
import { Store } from '@ngxs/store';

import { UserMinuteBalanceComponent } from '../user-minute-balance/user-minute-balance.component';

interface ModalData {
  user: User;
}

/**
 * Customer balance management — modal edition.
 *
 * Relocated out of the legacy `UserViewComponent` information tab so
 * balance top-up becomes a focused, transactional action with a
 * clear start and end instead of inline form state that lives as
 * long as the drawer does. The modal lives inside the `users` lib
 * (not `customer-360`) because it needs to compose the sibling
 * `UserMinuteBalanceComponent`, and importing across libs in both
 * directions would create a cycle.
 *
 * The form shape intentionally clones the legacy one
 * (operationType / transactionDate / paymentType branching for
 * cash / card / split / barter / bank-transfer / balance / comment)
 * so the backend call (`users/addBalanceNew`) keeps working unchanged.
 */
// Sane per-operation money ceiling; mirrors the backend MAX_MONEY_AMOUNT.
const MAX_MONEY_AMOUNT = 100_000_000;

@Component({
  selector: 'beautyapp-user-balance-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzModalModule,
    NzTabsModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzDatePickerModule,
    NzSelectModule,
    NzFormModule,
    TranslatePipe,
    UserMinuteBalanceComponent,
  ],
  template: `
    <div class="balance-modal">
      <nz-tabset [(nzSelectedIndex)]="activeTab" [nzAnimated]="true">
        <nz-tab [nzTitle]="minutesTitle">
          <ng-template #minutesTitle>
            <span nz-icon nzType="clock-circle"></span>
            {{ 'users.minutes' | translate }}
          </ng-template>
          <div class="tab-body">
            <beautyapp-user-minute-balance
              [user]="data.user"
              (balanceChange)="onBalanceChange()"
            ></beautyapp-user-minute-balance>
          </div>
        </nz-tab>

        <nz-tab [nzTitle]="amountTitle">
          <ng-template #amountTitle>
            <span nz-icon nzType="dollar"></span>
            {{ 'users.amount' | translate }}
          </ng-template>
          <div class="tab-body">
            <form
              nz-form
              [formGroup]="form"
              (ngSubmit)="submit()"
              nzLayout="vertical"
            >
              <div class="field-grid">
                <nz-form-item>
                  <nz-form-label>{{
                    'users.transactionDate' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-date-picker
                      formControlName="transactionDate"
                      [nzDisabledDate]="disableFuture"
                      class="w-full"
                    ></nz-date-picker>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item>
                  <nz-form-label>{{
                    'users.operationType' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-select formControlName="operationType" class="w-full">
                      <nz-option
                        nzValue="BALANCEIN"
                        [nzLabel]="'users.topUpBalance' | translate"
                      ></nz-option>
                      <nz-option
                        nzValue="BALANCERETURN"
                        [nzLabel]="'users.returnBalance' | translate"
                      ></nz-option>
                    </nz-select>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item>
                  <nz-form-label>{{
                    'users.balanceAmount' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-input-group nzPrefix="₾">
                      <input
                        nz-input
                        type="number"
                        formControlName="balance"
                        (blur)="roundMoney('balance')"
                      />
                    </nz-input-group>
                  </nz-form-control>
                </nz-form-item>

                <!--
                  beaut-517: the operator states up front whether money changed
                  hands. Everything below — payment type and the amount blocks
                  it reveals — stays hidden until they say it did, so a zero
                  amount can no longer stand for "nothing was paid".

                  No backticks in this comment: the whole template is a
                  template literal, and one would close it.
                -->
                <nz-form-item>
                  <nz-form-label nzRequired>{{
                    'users.moneyMode' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-select formControlName="withMoney" class="w-full">
                      <nz-option
                        [nzValue]="false"
                        [nzLabel]="'users.withoutMoney' | translate"
                      ></nz-option>
                      <nz-option
                        [nzValue]="true"
                        [nzLabel]="'users.withMoney' | translate"
                      ></nz-option>
                    </nz-select>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="withMoney()">
                  <nz-form-label>{{
                    'users.typePayment' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-select formControlName="paymentType" class="w-full">
                      <nz-option
                        [nzValue]="1"
                        [nzLabel]="'PAYMENTTYPE.1' | translate"
                      ></nz-option>
                      <nz-option
                        [nzValue]="2"
                        [nzLabel]="'PAYMENTTYPE.2' | translate"
                      ></nz-option>
                      <nz-option
                        [nzValue]="3"
                        [nzLabel]="'PAYMENTTYPE.3' | translate"
                      ></nz-option>
                      <nz-option
                        [nzValue]="6"
                        [nzLabel]="'PAYMENTTYPE.6' | translate"
                      ></nz-option>
                      <nz-option
                        [nzValue]="8"
                        [nzLabel]="'PAYMENTTYPE.8' | translate"
                      ></nz-option>
                    </nz-select>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="pt() === 1 || pt() === 3">
                  <nz-form-label>{{
                    'PAYMENTTYPE.1' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-input-group nzPrefix="₾">
                      <input
                        nz-input
                        type="number"
                        formControlName="cash"
                        (blur)="roundMoney('cash')"
                      />
                    </nz-input-group>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="pt() === 2 || pt() === 3">
                  <nz-form-label>{{
                    'PAYMENTTYPE.2' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-input-group nzPrefix="₾">
                      <input
                        nz-input
                        type="number"
                        formControlName="card"
                        (blur)="roundMoney('card')"
                      />
                    </nz-input-group>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="pt() === 6">
                  <nz-form-label>{{
                    'PAYMENTTYPE.6' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-input-group nzPrefix="₾">
                      <input
                        nz-input
                        type="number"
                        formControlName="barter"
                        (blur)="roundMoney('barter')"
                      />
                    </nz-input-group>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="pt() === 8">
                  <nz-form-label>{{
                    'calendar.form.bankTransfer' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-input-group nzPrefix="₾">
                      <input
                        nz-input
                        type="number"
                        formControlName="bankTransfer"
                        (blur)="roundMoney('bankTransfer')"
                      />
                    </nz-input-group>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item *ngIf="pt() === 2 || pt() === 3 || pt() === 8">
                  <nz-form-label>{{
                    'statistic.operation.column.bankName' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <nz-select
                      formControlName="bank"
                      nzAllowClear
                      class="w-full"
                      [nzPlaceHolder]="
                        'statistic.operation.search.bank' | translate
                      "
                    >
                      @for (b of banks; track b.value) {
                      <nz-option
                        [nzValue]="b.value"
                        [nzLabel]="b.label"
                      ></nz-option>
                      }
                    </nz-select>
                  </nz-form-control>
                </nz-form-item>

                <nz-form-item class="span-2">
                  <nz-form-label>{{
                    'users.comment' | translate
                  }}</nz-form-label>
                  <nz-form-control>
                    <textarea
                      nz-input
                      formControlName="comment"
                      rows="2"
                    ></textarea>
                  </nz-form-control>
                </nz-form-item>
              </div>

              <div class="actions">
                <button type="button" nz-button (click)="close()">
                  {{ 'buttons.close' | translate }}
                </button>
                <button
                  type="submit"
                  nz-button
                  nzType="primary"
                  [nzLoading]="saving()"
                  [disabled]="form.invalid"
                >
                  <span nz-icon nzType="check"></span>
                  {{ 'buttons.pay' | translate }}
                </button>
              </div>
            </form>
          </div>
        </nz-tab>
      </nz-tabset>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .balance-modal {
        padding: 4px 0 0;
      }
      .tab-body {
        padding-top: 16px;
      }
      .field-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px 16px;
      }
      .field-grid .span-2 {
        grid-column: span 2;
      }
      .w-full {
        width: 100%;
      }
      .actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid #eef0f3;
      }
      @media (max-width: 600px) {
        .field-grid {
          grid-template-columns: 1fr;
        }
        .field-grid .span-2 {
          grid-column: auto;
        }
      }
    `,
  ],
})
export class UserBalanceModalComponent {
  data = inject<ModalData>(NZ_MODAL_DATA);
  private modalRef = inject(NzModalRef);
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private notification = inject(NotificationService);
  private translate = inject(TranslateService);
  private store = inject(Store);

  activeTab = 0;
  saving = signal(false);
  form: FormGroup;

  private roles = this.store.selectSnapshot(
    (state: any) => state.auth.roles
  ) as string[];
  canEditDate = this.roles.includes('Admin') || this.roles.includes('Manager');

  disableFuture = (d: Date) => d.getTime() > Date.now();

  // Writable signal driven by the form's paymentType valueChanges. Was a
  // `computed(() => this.form.get('paymentType')?.value)` — but a non-signal
  // read inside computed() never re-evaluates, so the four *ngIf="pt() === N"
  // blocks for cash / card / barter / bankTransfer never rendered and the
  // cashier never saw a non-zero price. Wiring this as a signal + valueChanges
  // subscription makes the template react to payment-type selection.
  pt = signal<number | null>(null);

  // beaut-517. A signal, not a `computed()` over the form, for the reason the
  // comment above gives: a non-signal read inside computed() never
  // re-evaluates, so the block it gates would never appear.
  //
  // Starts null rather than false so the operator has to make the choice —
  // defaulting to either mode would put the decision back in the system's
  // hands, which is what the card is removing.
  withMoney = signal<boolean | null>(null);

  // Payment types that route money through a bank (card / split / bank
  // transfer) — the only ones that show/keep the bank selector.
  private readonly bankPaymentTypes = [2, 3, 8];

  /**
   * beaut-554. Which amount field each payment type is allowed to carry —
   * the same mapping the template already encodes in its `pt() === N` gates,
   * written down once so the two cannot drift.
   *
   * Split (3) owns TWO fields on purpose: it is the one type where cash and
   * card legitimately appear together, which is why the amounts cannot simply
   * be collapsed into a single control.
   */
  private readonly amountFieldsByPaymentType: Record<number, string[]> = {
    1: ['cash'],
    2: ['card'],
    3: ['cash', 'card'],
    6: ['barter'],
    8: ['bankTransfer'],
  };

  /** Every field an amount can be typed into. */
  private readonly amountFields = ['cash', 'card', 'barter', 'bankTransfer'];

  // Copied from libs/expense/src/lib/components/expense/expense.component.ts
  // (`banks`) so the top-up modal's bank picker matches the one already
  // used for salary/expense bank transfers.
  readonly banks: { value: Bank; label: string }[] = [
    { value: Bank.TBC, label: 'TBC Bank' },
    { value: Bank.BOG, label: 'Bank of Georgia' },
    { value: Bank.Credo, label: 'Credo Bank' },
    { value: Bank.Tera, label: 'Tera Bank' },
    { value: Bank.Cartu, label: 'Cartu Bank' },
    { value: Bank.Liberty, label: 'Liberty Bank' },
    { value: Bank.ProCredit, label: 'ProCredit Bank' },
    { value: Bank.SilkRoad, label: 'Silk Road Bank' },
    { value: Bank.BasisBank, label: 'Basis Bank' },
    { value: Bank.PashaBank, label: 'Pasha Bank' },
    { value: Bank.Other, label: 'Other' },
  ];

  constructor() {
    this.form = this.fb.group({
      operationType: ['BALANCEIN', Validators.required],
      transactionDate: [
        { value: new Date(), disabled: !this.canEditDate },
        Validators.required,
      ],
      // MAX_MONEY_AMOUNT caps each money field so absurd values (e.g. the
      // ~1.89e21 that poisoned the transaction totals) can't be submitted.
      // Mirrors the backend UserAddBalanceDto @Max. Negatives allowed for corrections.
      balance: [
        null,
        [
          Validators.required,
          Validators.min(-MAX_MONEY_AMOUNT),
          Validators.max(MAX_MONEY_AMOUNT),
        ],
      ],
      withMoney: [null, Validators.required],
      paymentType: [null, Validators.required],
      cash: [
        0,
        [Validators.min(-MAX_MONEY_AMOUNT), Validators.max(MAX_MONEY_AMOUNT)],
      ],
      card: [
        0,
        [Validators.min(-MAX_MONEY_AMOUNT), Validators.max(MAX_MONEY_AMOUNT)],
      ],
      bankTransfer: [
        0,
        [Validators.min(-MAX_MONEY_AMOUNT), Validators.max(MAX_MONEY_AMOUNT)],
      ],
      barter: [
        0,
        [Validators.min(-MAX_MONEY_AMOUNT), Validators.max(MAX_MONEY_AMOUNT)],
      ],
      bank: [null],
      comment: [null],
    });

    // beaut-517: switching to "without money" must leave nothing behind. The
    // payment type is cleared, which in turn hides every amount block (they are
    // gated on `pt()`), and the amounts themselves are zeroed — a hidden field
    // still submits its value, and a stray amount is exactly what the backend
    // now rejects.
    this.form.controls['withMoney'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        const withMoney = value as boolean | null;
        this.withMoney.set(withMoney);

        const paymentType = this.form.get('paymentType');
        if (withMoney) {
          paymentType?.setValidators(Validators.required);
        } else {
          // Required only makes sense in the mode that has the field.
          paymentType?.clearValidators();
          paymentType?.setValue(null);
          ['cash', 'card', 'bankTransfer', 'barter', 'bank'].forEach((name) =>
            this.form.get(name)?.setValue(name === 'bank' ? null : 0),
          );
        }
        paymentType?.updateValueAndValidity();
      });

    // beaut-554: changing the payment type must leave nothing behind either.
    // The amount blocks are gated on `pt()`, so switching type HIDES the
    // previous field — but a hidden Angular control keeps its value and still
    // submits it. The operator saw one amount on one method; the request
    // carried two, and the backend adds them up
    // (`operation.price = cash + card + bankTransfer`). Reported as 100 ₾ cash
    // switched to 100 ₾ bank transfer, stored as a single 200 ₾ operation —
    // in `transaction_new` as well, so the till was wrong too.
    //
    // This mirrors what the `withMoney` handler above already does, and for
    // the reason its comment gives. Only the fields the NEW type does not own
    // are cleared: split (3) keeps both cash and card, which is the whole
    // point of that type.
    this.form.controls['paymentType'].valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.pt.set(value as number | null);
        // Non-bank payment types hide the bank selector — clear it so a
        // stale bank never rides along in the submit payload.
        if (!this.bankPaymentTypes.includes(value as number)) {
          this.form.get('bank')?.setValue(null);
        }

        const owned = this.amountFieldsByPaymentType[Number(value)] ?? [];
        this.amountFields
          .filter((name) => !owned.includes(name))
          .forEach((name) =>
            this.form.get(name)?.setValue(0, { emitEvent: false }),
          );
      });
  }

  onBalanceChange(): void {
    // Minutes tab just changed the balance — signal the parent to refresh.
    this.modalRef.triggerOk();
  }

  // Round a money field to 2 decimals when it loses focus, mirroring the
  // backend numeric(10,2) rounding (beaut-340). Without this the user types
  // e.g. 0.69676, the backend silently stores 0.70, and the displayed value
  // never matches what was saved. Formatting on blur (not a validation error)
  // shows the real value up front while the server stays the source of truth.
  roundMoney(controlName: string): void {
    const control = this.form.get(controlName);
    const value = control?.value;
    if (value === null || value === undefined || value === '') return;
    const num = Number(value);
    if (Number.isNaN(num)) return;
    const rounded = Math.round(num * 100) / 100;
    if (rounded !== num) {
      control?.setValue(rounded);
    }
  }

  close(): void {
    this.modalRef.close();
  }

  submit(): void {
    if (this.form.invalid) return;
    const raw = this.form.getRawValue();
    const amount = Number(raw.balance);
    const currentBalance = Number(this.data.user?.balance ?? 0);
    if (raw.operationType === 'BALANCERETURN' && amount > currentBalance) {
      this.notification.error(
        this.translate.instant('users.balanceNotEnough'),
        this.translate.instant('users.upBalance')
      );
      return;
    }
    // beaut-517: with money the operator must have entered one. The backend
    // rejects a zero anyway; catching it here says so in the operator's own
    // language instead of as a 400.
    if (raw.withMoney && Number(raw.cash || 0) + Number(raw.card || 0) + Number(raw.bankTransfer || 0) === 0) {
      this.notification.error(
        this.translate.instant('users.moneyRequired'),
        this.translate.instant('users.upBalance')
      );

      return;
    }
    this.saving.set(true);
    // `withMoney` is a form field, not a request field — the mode travels as
    // `paymentType` being absent, which is why no column was added for it.
    const { withMoney, ...payload } = raw;
    this.userService
      .addBalance({
        ...payload,
        transactionDate: format(new Date(raw.transactionDate), 'yyyy-MM-dd'),
        balance: amount,
        price: withMoney ? Number(raw.cash || 0) + Number(raw.card || 0) : 0,
        userId: this.data.user.id,
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          if (raw.operationType === 'BALANCEIN') {
            this.notification.success(
              this.translate.instant('users.balanceFilledSuccessfully'),
              this.translate.instant('users.upBalance')
            );
          } else {
            this.notification.success(
              this.translate.instant('users.completedSuccessfully'),
              this.translate.instant('users.returnBalance')
            );
          }
          this.modalRef.triggerOk();
          this.modalRef.close(true);
        },
        error: () => this.saving.set(false),
      });
  }
}
